/**
 * HTTP-based storage adapter
 * Communicates with backend API for all conversation storage operations
 * This is the ONLY storage adapter - all chat history goes through backend
 */

import type {
  IStorageAdapter,
  StorageStats,
  Folder,
  TrashedConversation,
} from '@/types/storage.types';
import type { Conversation, Message } from '@/types/chat.types';
import { getBackendUrl as getRuntimeBackendUrl } from '@/config/runtimeConfig';
import { getBackendHeaders } from '../backendAuth';

/**
 * Get backend URL with priority:
 * 1. Runtime config (container startup injection - HIGHEST PRIORITY)
 * 2. Build-time env vars (development fallback)
 * 3. AdminContext localStorage (user override for testing only - LOWEST PRIORITY)
 */
function getBackendUrl(): string {
  // Priority 1 & 2: Runtime config (container env vars) > Build-time env vars
  const runtimeUrl = getRuntimeBackendUrl();
  if (runtimeUrl) {
    return runtimeUrl;
  }

  // Priority 3: AdminContext localStorage (fallback for testing/dev only)
  try {
    const adminConfigStr = localStorage.getItem('stellar_admin_config');
    if (adminConfigStr) {
      const adminConfig = JSON.parse(adminConfigStr);
      if (adminConfig.authDatabase?.backendUrl) {
        console.warn('[httpStorageAdapter] ⚠️ Using backend URL from localStorage (low priority):', adminConfig.authDatabase.backendUrl);
        return adminConfig.authDatabase.backendUrl;
      }
    }
  } catch (error) {
    console.warn('[httpStorageAdapter] Failed to load backend URL from admin config:', error);
  }

  // Final fallback
  return 'http://localhost:8000/api';
}

export class HttpStorageAdapter implements IStorageAdapter {
  private baseUrl: string;
  private initialized = false;

  constructor() {
    this.baseUrl = getBackendUrl();
  }

  async initialize(): Promise<void> {
    try {
      // Test connection to backend
      const healthUrl = this.baseUrl.replace('/api', '/health');
      const response = await fetch(healthUrl);

      if (!response.ok) {
        throw new Error('Backend API not available');
      }

      console.log('[HttpStorageAdapter] Connected to backend:', this.baseUrl);
      this.initialized = true;
    } catch (error) {
      throw new Error(`Failed to connect to backend: ${error}`);
    }
  }

  async saveConversation(conversation: Conversation): Promise<void> {
    this.ensureInitialized();

    // Create new conversation - use POST only
    // App.tsx should call updateConversation() directly for existing conversations
    const backendConversation = {
      id: conversation.id,
      title: conversation.title,
      messages: conversation.messages,
      agent_id: conversation.agentId,
    };

    try {
      await this.request('/conversations', {
        method: 'POST',
        body: JSON.stringify(backendConversation),
      });
    } catch (error) {
      // A 409 means a row with this id already exists — typically because a
      // slow/dropped response (gateway 502/503/504) triggered the caller's
      // create retry after the original POST had already landed, or a rapid
      // double-send raced the conversation list cache so the create path ran
      // twice. The create isn't idempotent, so recover by replaying the full
      // payload as an update: a no-op for the duplicate-retry case and a
      // newer-transcript recovery for the double-send case. The backend now
      // upserts and shouldn't 409 same-user creates, but this keeps older
      // backends (and genuine races) from surfacing a hard error.
      if ((error as Error).message.includes('409')) {
        await this.updateConversation(conversation.id, {
          title: conversation.title,
          messages: conversation.messages,
          agentId: conversation.agentId,
        });
        return;
      }
      throw error;
    }
  }

  async loadConversation(id: string): Promise<Conversation | null> {
    this.ensureInitialized();

    try {
      const response = await this.request(`/conversations/${id}`);
      const data = await response.json();
      return this.deserializeConversation(data);
    } catch (error) {
      if ((error as Error).message.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  async loadConversations(): Promise<Conversation[]> {
    this.ensureInitialized();

    const response = await this.request('/conversations');
    const data = await response.json();
    return data.map((conv: unknown) => this.deserializeConversation(conv));
  }

  async deleteConversation(id: string): Promise<void> {
    this.ensureInitialized();

    await this.request(`/conversations/${id}`, {
      method: 'DELETE',
    });
  }

  async updateConversation(
    id: string,
    updates: Partial<Conversation>
  ): Promise<void> {
    this.ensureInitialized();

    // Transform frontend updates to backend format
    const backendUpdates: Record<string, unknown> = {};

    if (updates.title !== undefined) {
      backendUpdates.title = updates.title;
    }
    if (updates.messages !== undefined) {
      backendUpdates.messages = updates.messages;
    }
    if (updates.agentId !== undefined) {
      backendUpdates.agent_id = updates.agentId;
    }
    if (updates.folderId !== undefined) {
      backendUpdates.folder_id = updates.folderId;
    }

    await this.request(`/conversations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(backendUpdates),
    });
  }

  async addMessage(
    conversationId: string,
    message: Message
  ): Promise<void> {
    this.ensureInitialized();

    // Single-call append: POST /conversations/:id/messages.
    // The legacy load + updateConversation path scaled linearly with
    // transcript length (full-array re-write on every assistant turn);
    // the dedicated endpoint pushes the append into Postgres via the
    // JSONB `||` operator, which is atomic at the row level.
    //
    // Other callers in this file (importData, clearAll) keep the
    // legacy load+update pattern intentionally — they operate on
    // whole conversations, not per-message appends.
    await this.request(
      `/conversations/${encodeURIComponent(conversationId)}/messages`,
      {
        method: 'POST',
        body: JSON.stringify({ message }),
      }
    );
  }

  async searchConversations(query: string): Promise<Conversation[]> {
    this.ensureInitialized();
    // Server-side title-only ILIKE substring match (user-scoped). The
    // 300ms debounce in ConversationHistory.tsx coalesces keystrokes, so
    // we deliberately don't add another debouncing layer here.
    // Empty/whitespace queries are a client-side concern — the call site
    // skips this method entirely when searchQuery.trim() is empty.
    const encoded = encodeURIComponent(query);
    const response = await this.request(`/conversations/search?q=${encoded}`);
    const data = await response.json();
    return data.map((conv: unknown) => this.deserializeConversation(conv));
  }

  async loadConversationsByAgent(agentId: string): Promise<Conversation[]> {
    this.ensureInitialized();

    // Backend doesn't have agent filter endpoint yet
    // Implement client-side filter as fallback
    const allConversations = await this.loadConversations();
    return allConversations.filter(conv => conv.agentId === agentId);
  }

  async exportData(): Promise<string> {
    this.ensureInitialized();

    // Backend doesn't have export endpoint yet
    // Implement client-side export as fallback
    const conversations = await this.loadConversations();
    const folders = await this.loadFolders();

    return JSON.stringify({
      version: '1.0',
      exportDate: new Date().toISOString(),
      conversations,
      folders,
    }, null, 2);
  }

  async importData(data: string): Promise<void> {
    this.ensureInitialized();

    // Backend doesn't have import endpoint yet
    // Implement client-side import as fallback
    try {
      const parsed = JSON.parse(data);

      if (parsed.conversations && Array.isArray(parsed.conversations)) {
        for (const conv of parsed.conversations) {
          await this.saveConversation(conv);
        }
      }

      if (parsed.folders && Array.isArray(parsed.folders)) {
        for (const folder of parsed.folders) {
          await this.createFolder(folder);
        }
      }
    } catch (error) {
      throw new Error(`Failed to import data: ${error}`);
    }
  }

  async clearAll(): Promise<void> {
    this.ensureInitialized();

    // Backend doesn't have clear all endpoint
    // Delete all conversations one by one
    const conversations = await this.loadConversations();
    for (const conv of conversations) {
      await this.deleteConversation(conv.id);
    }
  }

  async getStats(): Promise<StorageStats> {
    this.ensureInitialized();

    const response = await this.request('/conversations/stats/summary');
    const data = await response.json();

    return {
      totalConversations: data.total_conversations || 0,
      totalMessages: data.total_messages || 0,
      storageSize: data.storage_size || 0,
      oldestConversation: data.oldest_conversation
        ? new Date(data.oldest_conversation)
        : undefined,
      newestConversation: data.newest_conversation
        ? new Date(data.newest_conversation)
        : undefined,
    };
  }

  async createFolder(folder: Folder): Promise<void> {
    this.ensureInitialized();

    const backendFolder = {
      id: folder.id,
      name: folder.name,
      color: folder.color,
    };

    await this.request('/conversations/folders/create', {
      method: 'POST',
      body: JSON.stringify(backendFolder),
    });
  }

  async loadFolders(): Promise<Folder[]> {
    this.ensureInitialized();

    const response = await this.request('/conversations/folders/list');
    const data = await response.json();
    return data.map((folder: unknown) => this.deserializeFolder(folder));
  }

  async updateFolder(id: string, updates: Partial<Folder>): Promise<void> {
    this.ensureInitialized();

    const backendUpdates: Record<string, unknown> = {};

    if (updates.name !== undefined) {
      backendUpdates.name = updates.name;
    }
    if (updates.color !== undefined) {
      backendUpdates.color = updates.color;
    }
    if (updates.conversationIds !== undefined) {
      backendUpdates.conversation_ids = updates.conversationIds;
    }

    await this.request(`/conversations/folders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(backendUpdates),
    });
  }

  async deleteFolder(id: string): Promise<void> {
    this.ensureInitialized();

    await this.request(`/conversations/folders/${id}`, {
      method: 'DELETE',
    });
  }

  async addConversationToFolder(
    conversationId: string,
    folderId: string
  ): Promise<void> {
    this.ensureInitialized();

    // Backend doesn't have a separate endpoint for this
    // Update the conversation's folder_id field
    await this.updateConversation(conversationId, { folderId });
  }

  async removeConversationFromFolder(
    conversationId: string,
    _folderId: string
  ): Promise<void> {
    this.ensureInitialized();

    // Clear the conversation's folder_id. We can't go through
    // updateConversation() — its `updates.folderId !== undefined` filter
    // strips the unset signal, leaving the PUT body empty and the
    // backend returning 400 "No fields to update".
    await this.request(`/conversations/${conversationId}`, {
      method: 'PUT',
      body: JSON.stringify({ folder_id: null }),
    });
  }

  async loadTrash(): Promise<TrashedConversation[]> {
    this.ensureInitialized();

    const response = await this.request('/conversations/trash');
    return response.json() as Promise<TrashedConversation[]>;
  }

  async restoreConversation(id: string): Promise<void> {
    this.ensureInitialized();

    await this.request(
      `/conversations/${encodeURIComponent(id)}/restore`,
      { method: 'POST' }
    );
  }

  async purgeConversation(id: string): Promise<void> {
    this.ensureInitialized();

    await this.request(
      `/conversations/${encodeURIComponent(id)}/purge`,
      { method: 'DELETE' }
    );
  }

  private async request(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<Response> {
    // Includes Authorization + X-User-Email + X-User-Username for SSO users
    // (the backend needs the identity headers to keep the users table out
    // of the 'unknown' fallback state). Caller-provided headers win over
    // the defaults.
    const headers = await getBackendHeaders(
      options.headers as Record<string, string> | undefined
    );

    // SSRF guard (CWE-918): baseUrl comes from runtime config, which
    // Veracode treats as user-controlled. Resolve the final URL and verify
    // its origin AND that the resolved path stays under baseUrl's path
    // (so any unexpected absolute URL in `endpoint` — or a leading slash
    // that would escape `/api` — can't override the host or strip the
    // path prefix).
    //
    // We can't pass `endpoint` directly to `new URL(endpoint, base)`:
    // when endpoint starts with "/" it's treated as absolute-to-origin
    // and the base's pathname (e.g. "/api") is dropped, producing
    // `https://host/conversations` instead of `https://host/api/conversations`.
    // Normalize so the base path is always preserved.
    const baseWithSlash = this.baseUrl.endsWith('/')
      ? this.baseUrl
      : `${this.baseUrl}/`;
    const relativeEndpoint = endpoint.replace(/^\/+/, '');
    const targetUrl = new URL(relativeEndpoint, baseWithSlash);
    const expectedBase = new URL(baseWithSlash);
    if (
      targetUrl.origin !== expectedBase.origin ||
      !targetUrl.pathname.startsWith(expectedBase.pathname)
    ) {
      throw new Error(
        `Refusing to fetch unexpected URL ${targetUrl.toString()}`
      );
    }

    const response = await fetch(targetUrl.toString(), {
      ...options,
      headers,
    });

    if (!response.ok) {
      throw new Error(
        `API request failed: ${response.status} ${response.statusText}`
      );
    }

    return response;
  }

  private deserializeConversation(data: unknown): Conversation {
    const conv = data as Record<string, unknown>;
    // List endpoint omits `messages`; detail endpoint includes it.
    // When the field is missing, trust the backend's authoritative
    // `message_count` rather than recomputing from a length-0 array.
    const rawMessages = conv.messages as unknown[] | undefined;
    const messages = rawMessages
      ? rawMessages.map((msg) => this.deserializeMessage(msg))
      : [];
    const backendCount =
      typeof conv.message_count === 'number'
        ? conv.message_count
        : undefined;
    return {
      id: conv.id as string,
      title: conv.title as string,
      messages,
      messageCount: backendCount ?? messages.length,
      createdAt: new Date(conv.created_at as string),
      updatedAt: new Date(conv.updated_at as string),
      agentId: (conv.agent_id as string) || undefined,
      userId: conv.user_id as string,
      folderId: (conv.folder_id as string) || undefined,
    };
  }

  private deserializeMessage(data: unknown): Message {
    const msg = data as Record<string, unknown>;
    return {
      id: msg.id as string,
      role: msg.role as 'user' | 'assistant' | 'system',
      content: msg.content as string,
      timestamp: new Date(msg.timestamp as string),
      status: (msg.status as 'sending' | 'sent' | 'delivered' | 'error') || undefined,
      citations: msg.citations as Message['citations'],
      files: msg.files as Message['files'],
      // 👍/👎 feedback rating roundtrip — persisted on the message
      // JSON blob server-side. No schema change needed.
      feedback: msg.feedback as Message['feedback'],
      // Warnings (token truncation, PDF truncation, content filter, etc.)
      // were previously dropped on reload, even though the backend stored
      // them in the messages JSONB. Pull them back so users still see the
      // banner after refreshing or switching to a saved conversation.
      warning: msg.warning as Message['warning'],
      tokenUsage: msg.tokenUsage as Message['tokenUsage'],
    };
  }

  private deserializeFolder(data: unknown): Folder {
    const folder = data as Record<string, unknown>;
    return {
      id: folder.id as string,
      name: folder.name as string,
      color: (folder.color as string) || undefined,
      createdAt: new Date(folder.created_at as string),
      updatedAt: new Date(folder.updated_at as string),
      userId: folder.user_id as string,
      conversationIds: (folder.conversation_ids as string[]) || [],
    };
  }

  private ensureInitialized(): void {
    if (!this.initialized) {
      throw new Error('HttpStorageAdapter not initialized');
    }
  }
}
